import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import type { FilteredInfo, RequestParams, TableChange } from '../../../models/table';
import { SortedInfo } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

export type AdvocateFeedback = {
  chat_id: string;
  timestamp: string;
  feedback: string | null;
  advocate_name: string;
  training: string;
  total_count: number;
  rating: string;
  guest_org_name: string;
  advocate_org_name: string;
  guest_org_timezone_name: string;
  advocate_org_timezone_name: string;
  starred: boolean;
};

type AdvocateFeedbackSliceType = {
  advocateFeedback: AdvocateFeedback[];
  advocateFeedbackParam: RequestParams<AdvocateFeedback>;
  apiStatus: APIStatus;
};

const initialState: AdvocateFeedbackSliceType = {
  advocateFeedback: [],
  advocateFeedbackParam: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdvocateFeedback>('timestamp', 'descend'),
    filteredInfo: {},
  },
  apiStatus: APIStatus.IDLE,
};

export const changeAdvocateFeedbackRating = createAsyncThunk<
  string,
  { chat_id: string; value: string },
  AsyncThunkConfig
>('advocateFeedback/changeAdvocateFeedbackRating', async (rating, thunkAPI) => {
  try {
    const response = (await axios.post('/v0_set_advocate_feedback_rating', rating)) as string; // chat_id;
    thunkAPI.dispatch(fetchAdvocateFeedback());
    if (response) {
      notification.success({
        title: 'Success',
        description: 'Rating has been successfully changed.',
      });
    }
    return response;
  } catch (e) {
    logError(e, ['advocateFeedbackSlice', 'changeAdvocateFeedbackRating']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export type AdvocateFeedbackStarred = { chat_id: string; starred: boolean };
export const changeAdvocateFeedbackStarred = createAsyncThunk<string, AdvocateFeedbackStarred, AsyncThunkConfig>(
  'advocateFeedback/changeAdvocateFeedbackStarred',
  async (data, thunkAPI) => {
    try {
      const response = (await axios.post('/v0_star_advocate_feedback', data)) as string; // chat_id
      thunkAPI.dispatch(fetchAdvocateFeedback());
      if (response) {
        notification.success({
          title: 'Success',
          description: `Feedback was ${data.starred ? 'starred' : 'unstarred'} successfully.`,
        });
      }
      return response;
    } catch (e) {
      logError(e, ['advocateFeedbackSlice', 'changeAdvocateFeedbackStarred']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAdvocateFeedback = createAsyncThunk<AdvocateFeedback[], undefined, AsyncThunkConfig>(
  'advocateFeedback/fetchAdvocateFeedback',
  async (_, thunkAPI) => {
    try {
      const advocateFeedbackParam = thunkAPI.getState().advocateFeedbackSlice.advocateFeedbackParam;
      const response = (await axios.post('v0_get_advocate_feedback', advocateFeedbackParam)) as AdvocateFeedback[];
      return response ?? [];
    } catch (e) {
      logError(e, ['advocateFeedbackSlice', 'fetchAdvocateFeedback']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const advocateFeedbackSlice = createSlice({
  name: 'advocateFeedback',
  initialState,
  reducers: {
    handleFeedbackTableChange: (state, action: PayloadAction<TableChange<AdvocateFeedback>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdvocateFeedback>;
      state.advocateFeedbackParam.pagination = action.payload.pagination;
      state.advocateFeedbackParam.sortedInfo = sorter;
      state.advocateFeedbackParam.filteredInfo = removeEmpty(filters);
    },
    clearFilter: (state) => {
      state.advocateFeedbackParam.pagination = initialState.advocateFeedbackParam.pagination;
      state.advocateFeedbackParam.sortedInfo = initialState.advocateFeedbackParam.sortedInfo;
      state.advocateFeedbackParam.filteredInfo = initialState.advocateFeedbackParam.filteredInfo;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdvocateFeedback.pending, (state, _action) => {
        state.apiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdvocateFeedback.fulfilled, (state, action) => {
        state.advocateFeedback = action.payload;
        state.apiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdvocateFeedback.rejected, (state, _action) => {
        state.apiStatus = APIStatus.ERROR;
      });
  },
});

export const { handleFeedbackTableChange, clearFilter } = advocateFeedbackSlice.actions;
